// anlageUpdatePayload.ts — Speichern des Bearbeiten-Modus der Akte
// („Allgemein" = Anlage-Formular, docs/analyse-akte-allgemein-gleich-anlage.md, C1/C2/C4).
//
// Das Formular merkt sich den geladenen Stand (`geladen`, aus contactToFormState)
// und kennt den aktuellen (`aktuell`). Beide laufen durch DENSELBEN Builder wie
// die Neuanlage; verglichen wird das Ergebnis, nicht der Formular-State. So
// entscheidet genau eine Stelle, was ein Feld in der Datenbank bedeutet, und
// ein Feld, das der Anwender nicht angefasst hat, geht nie mit — auch dann
// nicht, wenn die Rückabbildung einen Bestandswert anders schreibt, als der
// Builder ihn zurückgäbe.
//
// Regeln:
//   - Skalare nur, wenn geändert. Ein geleertes Feld lassen die Builder weg —
//     hier wird daraus `null` (bzw. `false` bei den Schaltern „verstorben"
//     und „Newsletter"), sonst ließe es sich nie leeren.
//   - `communications` / `addresses` / `bankAccounts` nur, wenn sich die Liste
//     geändert hat, dann VOLLSTÄNDIG und mit `id` je Bestandszeile. Das Backend
//     (contactKindlisten.write.ts) entfernt, was darin fehlt.
//   - `zuwendungen` / `verfuegungen` / `todesfallBelege` werden serverseitig
//     komplett abgeglichen — deshalb ebenfalls nur bei Änderung (C4). Die
//     Todesfall-Belege nur, solange „verstorben“ gilt (sie bleiben beim
//     Zurücknehmen stehen).
//   - Was das Formular nicht verwaltet, steht nie im Rumpf: Status (Header),
//     Verantwortlichkeit und „persönlich" (eigene Endpunkte), der Typ, und die
//     Felder aus Analyse B14, die weder Akte noch Anlage zeigen.

import type { FormState } from "./formUtils";
import { buildPrivateContactCreatePayload } from "./buildPrivateContactCreatePayload";
import { buildCompanyContactCreatePayload } from "./buildCompanyContactCreatePayload";
import type { BranchengruppeZeile } from "./contactToFormState";

export type AnlageTyp = "Person" | "Firma";

type Rec = Record<string, unknown>;

/** Kindlisten mit eigenem Zeilen-Abgleich im Backend. */
const LISTEN = ["communications", "addresses", "bankAccounts"] as const;

/** Listen, die das Backend beim PUT komplett ersetzt. */
const ERSETZT = ["zuwendungen", "verfuegungen", "todesfallBelege"] as const;

/**
 * Nie im Rumpf des Bearbeiten-Modus.
 *
 * Status und Verantwortlichkeit gehören in den Header und haben eigene Wege
 * (`/ownership`, Status-Endpunkte); der PUT strippt FO/BO ohnehin. Der Typ
 * ändert sich nur über die Umwandlung. Die Sitz-Felder `street` … `country`
 * stehen im Firmen-`basePayload` nur für den Staged-Fallback der Anlage und
 * sind keine Kontaktspalten.
 */
const NIE_IM_RUMPF = new Set([
  "statusName",
  "partnerProfile",
  "weitereStatus",
  "persoenlich",
  "foUserId",
  "foUserIds",
  "boType",
  "boUserId",
  "boTeamId",
  "inhouseContactUserId",
  "type",
  "typ",
  "contactType",
  "street",
  "postalCode",
  "city",
  "ortsteil",
  "country",
]);

/**
 * Analyse B14: weder in der Akte noch in der Anlage sichtbar. Die Builder
 * erzeugen sie nicht — die Liste ist die zweite Sicherung, damit ein künftiger
 * Builder-Umbau sie nicht still mit dem Diff leert.
 */
export const B14_FELDER = [
  "coArten",
  "coNotiz",
  "besonderheiten",
  "gruender",
  "gruendungsjahr",
  "qualitaetsmanagementVorhanden",
  "geschaeftsjahrAbweichend",
  "taxDeductible",
  "householdRole",
  "tags",
  "phase",
  "segmentType",
] as const;
const B14 = new Set<string>(B14_FELDER);

/** Leerwert je Feld, wenn der Builder es nicht mehr liefert. */
const LEERWERT: Record<string, Rec> = {
  // Schalter: Aus heißt `false`, nicht „unbekannt".
  deceased: { deceased: false },
  newsletter: { newsletter: false },
  // Katalogverweise laufen per Name; geleert wird die Relation selbst.
  salutationName: { salutation: null },
  titleName: { title: null },
};

/** Stabiler Vergleich (Schlüsselreihenfolge egal). */
const gleich = (a: unknown, b: unknown): boolean => stabil(a) === stabil(b);
const stabil = (v: unknown): string =>
  JSON.stringify(v, (_k, x) =>
    x && typeof x === "object" && !Array.isArray(x)
      ? Object.fromEntries(Object.entries(x as Rec).sort(([p], [q]) => p.localeCompare(q)))
      : x,
  );

const bauen = (typ: AnlageTyp, state: FormState, firmaAutomatisch = true): Rec => {
  if (typ === "Person") {
    return (
      buildPrivateContactCreatePayload(state, undefined, undefined, { allowIncomplete: true, firmaAutomatisch }) ?? {}
    );
  }
  return buildCompanyContactCreatePayload(state, undefined, { allowIncomplete: true })?.payloadWithDetails ?? {};
};

/** Haupt- plus weitere Branchen im Format von `branchengruppenSpeichern`. */
const branchenAus = (state: FormState): BranchengruppeZeile[] => {
  const zeilen: BranchengruppeZeile[] = [];
  const haupt = Number(state.branchengruppeId);
  if (Number.isInteger(haupt) && haupt > 0) {
    zeilen.push({ branchengruppeId: haupt, freitext: String(state.brancheFreitext ?? "").trim() || null });
  }
  try {
    const weitere = JSON.parse(String(state.weitereBranchengruppen ?? "[]"));
    if (Array.isArray(weitere)) {
      for (const w of weitere) {
        const id = Number(w?.branchengruppeId);
        if (Number.isInteger(id) && id > 0) {
          zeilen.push({ branchengruppeId: id, freitext: String(w?.freitext ?? "").trim() || null });
        }
      }
    }
  } catch {
    /* kaputter Zwischenstand → nur die Hauptbranche */
  }
  return zeilen;
};

export interface AnlageUpdateErgebnis {
  /** Rumpf für `PUT contact/:id` — leer, wenn sich nichts geändert hat. */
  body: Rec;
  /**
   * Firma: die vollständige Branchenliste für `branchengruppenSpeichern`, aber
   * nur, wenn sie sich geändert hat. Sonst `null` (nichts schreiben).
   */
  branchengruppen: BranchengruppeZeile[] | null;
  /** Gibt es überhaupt etwas zu speichern? */
  geaendert: boolean;
}

export function buildAnlageUpdatePayload(args: {
  typ: AnlageTyp;
  geladen: FormState;
  aktuell: FormState;
}): AnlageUpdateErgebnis {
  const { typ, geladen, aktuell } = args;
  const alt = bauen(typ, geladen);
  const neu = bauen(typ, aktuell);
  const body: Rec = {};

  const listen = new Set<string>([...LISTEN, ...ERSETZT]);
  const schluessel = new Set([...Object.keys(alt), ...Object.keys(neu)]);
  for (const k of schluessel) {
    if (NIE_IM_RUMPF.has(k) || B14.has(k) || listen.has(k)) continue;
    if (gleich(alt[k], neu[k])) continue;
    if (k in neu) {
      body[k] = neu[k];
      continue;
    }
    // Das Geschlecht folgt der Anrede. Wird die Anrede geleert, bleibt es
    // stehen: Ein gepflegtes „Divers" soll nicht mit ihr verschwinden.
    if (k === "gender") continue;
    Object.assign(body, LEERWERT[k] ?? { [k]: null });
  }

  // Firma: Eine geleerte Rechtsform käme als "" — für die Relation kein Wert.
  if (body.legalForm === "") body.legalForm = null;

  for (const k of [...LISTEN, ...ERSETZT]) {
    // Belege zum Todesfall nur, solange „verstorben“ gilt: Der Builder lässt
    // sie ohne das Flag weg — ein leerer Vergleichsstand hieße sonst „alle
    // entfernen“. Beim Zurücknehmen von „verstorben“ bleiben sie stehen.
    if (k === "todesfallBelege" && neu.deceased !== true) continue;
    const a = alt[k] ?? [];
    const n = neu[k] ?? [];
    if (!gleich(a, n)) body[k] = n;
  }

  // Automatische Firma geschäftlicher Wege (geschaeftlichFirma.ts): Ladestand
  // und aktueller Stand zeigen sie beide — sie allein ist keine Änderung (kein
  // Leave-Dialog nur durchs Öffnen). Wird aber ohnehin gespeichert, geht sie
  // mit: Weicht die Liste vom Ladestand OHNE Automatik ab, reist sie mit.
  if (typ === "Person" && Object.keys(body).length > 0 && !("communications" in body)) {
    const ohneAutomatik = bauen(typ, geladen, false).communications ?? [];
    const n = neu.communications ?? [];
    if (!gleich(ohneAutomatik, n)) body.communications = n;
  }

  let branchengruppen: BranchengruppeZeile[] | null = null;
  if (typ === "Firma") {
    const a = branchenAus(geladen);
    const n = branchenAus(aktuell);
    if (!gleich(a, n)) branchengruppen = n;
  }

  return {
    body,
    branchengruppen,
    geaendert: Object.keys(body).length > 0 || branchengruppen !== null,
  };
}
